import { faSignIn, faLock } from '@fortawesome/pro-solid-svg-icons'
import language from 'services/Language'
import {
  EventFieldsFragment,
  BaseCollectiveQueryResult,
  CollectiveTokenType
} from 'graphql/generated'
import React, { FormEvent, useCallback, useEffect, useState } from 'react'
import { isValidEmail } from 'lib/helpers'
import Link from 'next/link'
import useResolveEns from 'hooks/collectives/useResolveEns'
import PasswordValidation from 'components/PasswordValidation/PasswordValidation'
import CollectiveClosedNotice from './CollectiveClosedNotice'
import EmailUsedNotice from './EmailUsedNotice'
import Button from 'components/Button'
import usePasswordSignup from 'hooks/signup/usePasswordSignup'
import useEphemeralCryptoAuth from 'hooks/collectives/useEphemeralCryptoAuth'
import useToasts from 'hooks/useToasts'
import { faEthereum } from '@fortawesome/free-brands-svg-icons'
import TrackableClick from 'components/common/TrackableClick'
import Image from 'next/image'

const tosUrl = `https://upstreamapp.com/tos`
const privacyPolicyUrl = `https://upstreamapp.com/privacy`

interface IWalletProps {
  address: string
  signature: string
  ephemeralCryptoAuthId: string
}

interface IProps {
  event?: EventFieldsFragment
  collective?: Maybe<BaseCollectiveQueryResult['collective']>
  wallet?: IWalletProps
  onboarding?: Maybe<KVMap>
}

function SignupForm({ event, collective, ...props }: IProps) {
  const title = event?.title || collective?.name

  const [isPasswordFocused, setIsPasswordFocused] = useState(false)
  const [isPasswordValid, setIsPasswordValid] = useState(false)
  const [walletLogginIn, setWalletLoggingIn] = useState(false)
  const { verifyWalletAndLogin } = useEphemeralCryptoAuth()
  const { addToast } = useToasts()

  const [wallet, setWallet] = useState(props.wallet)
  useEffect(() => {
    setWallet(props.wallet)
  }, [props.wallet])

  const signInWithWallet = async () => {
    setWalletLoggingIn(true)
    try {
      const resp = await verifyWalletAndLogin()
      if (resp) {
        setWalletLoggingIn(false)
      }
    } catch (e) {
      addToast(e.message, { appearance: 'warning' })
      setWalletLoggingIn(false)
    }
  }

  const {
    email,
    loading: loadingPassword,
    password,
    setEmail,
    setPassword,
    setRawUsername,
    setUsername,
    signup,
    username,
    errorMessage
  } = usePasswordSignup(event, collective, wallet)

  const emailUsedError = errorMessage === 'Email is already used.'

  const resolveEns = useResolveEns()
  useEffect(() => {
    if (!wallet?.address) {
      return
    }

    resolveEns(wallet.address).then(ens => {
      if (ens) {
        setRawUsername(ens)
      }
    })
  }, [resolveEns, wallet?.address, setRawUsername])

  const canContinue =
    !!(
      isValidEmail(email.trim()) &&
      password.trim() &&
      isPasswordValid &&
      username.trim()
    ) &&
    (!collective || !!wallet) &&
    !loadingPassword

  const onPasswordSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault()
      if (!canContinue) {
        return
      }
      signup()
    },
    [canContinue, signup]
  )

  if (
    !!collective &&
    collective.governanceType === CollectiveTokenType.OWNED &&
    !collective.parameters.enableNewMembershipRequests
  ) {
    return <CollectiveClosedNotice collective={collective} />
  }

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex flex-1 items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-6">
          <div>
            <Image
              className="mx-auto mt-3 h-8 w-auto"
              src="/static/new-logo-dark-horizontal@2x.png"
              alt="Upstream"
              width={121}
              height={32}
            />
            <h2 className="mt-6 text-center text-xl font-medium text-gray-900">
              {`Create an Account ${title ? `\u00b7 ${title}` : ''}`}
            </h2>
          </div>

          <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <form className="space-y-4" onSubmit={onPasswordSubmit}>
                <TrackableClick
                  event={`Wallet Signup Button Clicked`}
                  props={
                    collective
                      ? {
                          collectiveId: collective.id,
                          collectiveName: collective.name
                        }
                      : {}
                  }
                >
                  <Button
                    label="Connect Web3 Wallet"
                    icon={faEthereum}
                    type="button"
                    color={'white'}
                    disabled={loadingPassword || walletLogginIn}
                    onClick={signInWithWallet}
                    loading={walletLogginIn}
                    className="border border-gray-300 rounded-md w-full py-3 text-gray-500"
                  />
                </TrackableClick>
                {!collective && (
                  <>
                    <div className="relative mt-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200" />
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-400">or</span>
                      </div>
                    </div>

                    <div className="space-y-5 mb-2">
                      <div>
                        <label
                          htmlFor="email-address"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {`Email address `}
                          <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="email"
                          autoComplete="email"
                          required
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          value={email}
                          onChange={setEmail}
                          disabled={loadingPassword}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="email-address"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {`Username `}
                          <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="text"
                          autoComplete="username"
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          value={username}
                          onChange={setUsername}
                          disabled={loadingPassword}
                        />
                      </div>

                      <div>
                        <label
                          htmlFor="password"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {`Password `}
                          <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="password"
                          autoComplete="current-password"
                          required
                          className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          value={password}
                          onChange={setPassword}
                          disabled={loadingPassword}
                          onFocus={() => setIsPasswordFocused(true)}
                          onBlur={() => setIsPasswordFocused(false)}
                        />

                        <PasswordValidation
                          password={password}
                          isPasswordFocused={isPasswordFocused}
                          onValidityChange={setIsPasswordValid}
                          className="mt-3"
                        />
                      </div>

                      <Button
                        type={'submit'}
                        disabled={!canContinue}
                        loading={loadingPassword}
                        icon={faLock}
                        label={'Create an Account'}
                        className="w-full"
                        size="lg"
                        color="blue"
                      />

                      {!!errorMessage && !emailUsedError && (
                        <p className="text-red-500 text-center font-medium transition">
                          {errorMessage}
                        </p>
                      )}

                      {emailUsedError && <EmailUsedNotice email={email} />}

                      <div>
                        <p className="text-gray-400 font-light text-xs">
                          {`By continuing, you agree to Upstream's `}
                          <Link
                            target={'_blank'}
                            className="text-blue"
                            href={tosUrl}
                          >
                            {`Terms of Service`}
                          </Link>
                          {` and `}
                          <Link
                            target={'_blank'}
                            className="text-blue"
                            href={privacyPolicyUrl}
                          >
                            {`Privacy Policy`}
                          </Link>
                          {`.`}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </div>

          <div className="pt-1 text-center">
            <Link href="/auth/login">
              <Button
                label={language.common.loginInstead}
                color="textOnly"
                icon={faSignIn}
                iconPlacement="after"
              />
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SignupForm
